<!-- Copyright: Sean O'Donoghue & Sandeep Kaur -->
<!-- <ProgramParse
    v-if="timeZoneSelected"
    :key="timeZoneSelected"
    :time-zone-selected="timeZoneSelected"
    :session="session"
    @finished="(payload) => (program = payload)" /> -->

<!-- eslint-disable vue/no-v-html -->
<template>
  <TimeZone
    v-if="!props.hideTimeZone"
    :time-zone-selected="timeZoneSelected"
    :day-selected="daySelectedForHash"
    @update="switchTimeZone" />
  <div v-if="Object.keys(program).length > 0" id="programTable">
    <DayTags
      v-if="showDayTags"
      :program-days="getSortedDays()"
      :program="program"
      :time-zone-selected="timeZoneSelected"
      :day-selected="validSelectedDays"
      @update="toggleDayView"
      @toggle-day="toggleDaySelection" />
    <!-- the grey camera (VideoIcon.vue) and the play icon (TableRows), each only when it appears
         here; shorter on narrow screens (Seán, 2026-10-29) -->
    <ul v-if="hasOnlineEvents || hasRecordedEvents" class="legend">
      <li v-if="hasOnlineEvents">
        <img :src="onlineIconSrc" class="streamed" alt="" />
        <span class="wideScreenOnly">Live online event</span>
        <span class="narrowScreenOnly">Online event</span>
      </li>
      <li v-if="hasRecordedEvents">
        <VideoPlayIcon class="legendPlay" />
        <template v-if="isWatchInAdvance">Full-length recorded talk (watch in advance)</template>
        <template v-else>Recorded talk</template>
      </li>
    </ul>
    <!-- each day under its bar: the days filtered (DayTags), green; else every day (Seán,
         2026-10-29: filters, no accordions). A new choice slides in sideways, as the pills above
         move: from the right for a later event, from the left for an earlier one. Back to every
         day: the days shown stay, the others grow in above and below them -->
    <div ref="daysRef" class="days">
      <Transition :name="slideName">
        <TransitionGroup :key="listKey" tag="div" name="dayGrow" class="dayList">
          <div v-for="day in shownDays" :key="day" class="dayBlock">
            <div class="dayBlockInner">
              <h5 class="dayBar" :class="{selected: validSelectedDays.includes(day)}">
                <span class="dayBarDate">{{ getDayHeading(day) }}:</span>
                <span class="dayBarTitle" v-html="program[day].title" />
              </h5>
              <table class="eventTable">
                <colgroup>
                  <col class="first" />
                  <col class="second" />
                  <col class="third" />
                </colgroup>
                <TableRows
                  :program-day="program[day]"
                  :session="session"
                  :description="description"
                  :time-zone-selected="timeZoneSelected"
                  :video-owners="videoOwners" />
              </table>
            </div>
          </div>
        </TransitionGroup>
      </Transition>
    </div>
  </div>
</template>

<script setup>
// eslint-disable-next-line
const log = (msg) => process.env.VUE_APP_DEV && console.log(`Table: ${msg}`);

const props = defineProps([
  "description",
  "showDayTags",
  "session",
  "hideTimeZone",
  "dateFrom",
  "dateTo"
]);

import {computed, nextTick, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import useProgram, {getDefaultTimeZone} from "@/composables/useProgram";
import {sheetVersion} from "@/composables/useProgramSheet";
import useTimeZoneSelected from "@/composables/useTimeZoneSelected";
import useUserSafe from "@/composables/useUserSafe";
import TableRows from "@/components/Program/TableRows";
import TimeZone from "@/components/Program/TimeZone";
import DayTags from "@/components/Program/DayTags";
import useShowcaseVideo from "@/composables/useShowcaseVideo";
import {getServerNow} from "@/composables/useServerClock";
import {getEventDays, getIsEventName, getProgramEvents} from "@/composables/programEvents";
import VideoPlayIcon from "@/components/Program/VideoPlayIcon";
import useOpenDayModes from "@/composables/useOpenDayModes";
import useKeepDaysInView from "@/composables/useKeepDaysInView";

const selectedDays = ref([]);
const timeZoneSelected = useTimeZoneSelected();
const program = ref({});
const route = useRoute();
const {getVideoOwners} = useShowcaseVideo();

// an older link's day ("Tue 14": weekday and day, keys until 2026-10-11) → the one program day
// that's that weekday and day ("Apr 14"); none, or more than one: dropped
function getLegacyDay(day) {
  const [weekday, dayNumber] = day.split(" ");
  const getIsMatch = (key) => {
    const [dateWeekday, , dateDay] = (program.value[key]?.date || "").split(" ");
    return dateWeekday === `${weekday},` && dateDay === `${dayNumber},`;
  };
  const matches = Object.keys(program.value).filter(getIsMatch);
  return matches.length === 1 ? matches[0] : null;
}

// days restored from the URL: current keys kept, an event's name its days (#online-workshop:
// programEvents.js), older keys resolved, others dropped (the hash is rewritten from these, so the
// URL is cleaned up too)
function getResolvedDays(days) {
  const resolved = days.flatMap((day) => {
    if (program.value[day]) return [day];
    if (getIsEventName(day)) return getEventDays(program.value, day);
    return [getLegacyDay(day)];
  });
  return [...new Set(resolved.filter(Boolean))];
}

const validSelectedDays = computed(() => getResolvedDays(selectedDays.value));

// the days shown: those selected (DayTags' filters), else every day
const shownDays = computed(() => {
  const days = getSortedDays();
  if (!validSelectedDays.value.length) return days;
  return days.filter((day) => validSelectedDays.value.includes(day));
});

// the slide's direction: later days (by the first, else the last) slide in from the right. Back
// to every day, no slide (the list's key kept): the other days grow in around those shown
const slideName = ref("slideLeft");
const listKey = ref("");
watch(shownDays, (days) => {
  if (!validSelectedDays.value.length && listKey.value) return;
  listKey.value = days.join();
}, {immediate: true});
watch(shownDays, (days, previousDays) => {
  const order = getSortedDays();
  const getShift = (pick) => order.indexOf(pick(days)) - order.indexOf(pick(previousDays || []));
  const shift = getShift((list) => list[0]) || getShift((list) => list.at(-1));
  slideName.value = shift < 0 ? "slideRight" : "slideLeft";
});

const getOpenModes = () =>
  shownDays.value.flatMap((day) => (program.value[day]?.events || []).map((event) => event.mode));
const isWatchInAdvance = useOpenDayModes(getOpenModes);
const daysRef = ref(null);
useKeepDaysInView(daysRef, shownDays);

// once the program has loaded, the selection itself holds only current keys (so a later click
// adds or removes the right day)
let isEventLink = false; // the address named an event: its days open, over or not
watch([selectedDays, program], () => {
  if (!Object.keys(program.value).length) return;
  if (selectedDays.value.some(getIsEventName)) isEventLink = true;
  const resolved = getResolvedDays(selectedDays.value);
  if (resolved.join() !== selectedDays.value.join()) selectedDays.value = resolved;
});

// a day is over once its last item has ended (the server's now, as the banner's countdown)
function getIsDayOver(day, now) {
  const ends = (program.value[day]?.events || []).map((event) => event.end || event.start);
  const last = ends.filter((end) => end?.isValid).sort((a, b) => b - a)[0];
  return Boolean(last) && last < now;
}

// the Program page opens on what's ahead (Seán, 2026-10-22): the address's days that aren't over
// (a shared link to the Workshop still works); else the next event, all its days (Seán,
// 2026-10-29: the Online Masterclass); else, every event over, all days. Once, as the program first loads: the address's
// days (DayTags, as it's created) are in by then; a later time zone change keeps the choice
// the last day choice on this page, for the tab (Seán, 2026-11-03): an address without days
// (Back: the router puts back the address it knew, without the days written in silently) opens
// on it, not the default; a link with days still opens on those
const daysKey = `programDays:${route.path}`;
function getRememberedDays() {
  try {
    const days = JSON.parse(sessionStorage.getItem(daysKey));
    return Array.isArray(days) ? days.filter((day) => day in program.value) : null;
  } catch {
    return null;
  }
}
watch(selectedDays, (days) => {
  if (hasOpened) sessionStorage.setItem(daysKey, JSON.stringify(days));
});

let hasOpened = false;
async function openOnUpcoming() {
  if (hasOpened || !props.showDayTags || !Object.keys(program.value).length) return;
  const isFromAddress = selectedDays.value.length > 0;
  hasOpened = true;
  await nextTick();
  if (isEventLink) return; // a named event's days, as resolved, even once it's over
  const now = getServerNow();
  const upcoming = getSortedDays().filter((day) => !getIsDayOver(day, now));
  const kept = getResolvedDays(selectedDays.value).filter((day) => upcoming.includes(day));
  if (kept.length) return (selectedDays.value = kept);
  const remembered = isFromAddress ? null : getRememberedDays();
  if (remembered) return (selectedDays.value = remembered);
  const events = getProgramEvents(program.value, getSortedDays());
  selectedDays.value = events.find((event) => upcoming.includes(event.at(-1))) || [];
}
watch(program, openOnUpcoming);

// comma-separated, in schedule order; single-day URLs keep their existing form
const daySelectedForHash = computed(() => {
  const days = getSortedDays().filter((day) => validSelectedDays.value.includes(day));
  return days.join(",");
});

const onlineIconSrc = require("@/assets/img/onsite+online.png");

function getAllEvents() {
  return Object.values(program.value || {}).flatMap((day) => day.events || []);
}

function getProgramYear() {
  const firstDay = Object.values(program.value || {})[0];
  return String(firstDay?.date || "").match(/(\d{4})\s*$/)?.[1] || "";
}

// which program event records each library video: the earliest that matches it, across all
// days (an April talk, not a later summary of it); the rows use it for the play icon
const videoOwners = computed(() => getVideoOwners(getAllEvents(), getProgramYear()));

const hasOnlineEvents = computed(() => {
  return getAllEvents().some((event) => /online/i.test(event.mode || ""));
});

const hasRecordedEvents = computed(() => {
  if (videoOwners.value.size) return true;
  return getAllEvents().some((event) => event.video || event.Video);
});

function getSortedDays() {
  const getDayTimestamp = (dayKey) => {
    const dateString = program.value?.[dayKey]?.date;
    const timestamp = Date.parse(dateString || "");
    return Number.isFinite(timestamp) ? timestamp : 0;
  };
  let sortedDays = Object.keys(program.value).sort(
    (a, b) => getDayTimestamp(a) - getDayTimestamp(b)
  );
  log(`Program/Table: sortedDays = ${sortedDays}`);
  return sortedDays;
}

function formatDayForToggleDisplay(day) {
  log(`Program/Table: formatDayForToggleDisplay(${day})`);
  let arr = day.split(" ");
  return arr[0] + " " + arr[1].slice(0, 3) + " " + arr[2] + " " + arr[3];
}

// "Wed Nov 4 2026 (Sydney local time)": the note when the day falls on another date than the
// program's (useProgram getDayNote)
function getDayHeading(dayKey) {
  const note = program.value[dayKey]?.localNote;
  const heading = formatDayForToggleDisplay(program.value[dayKey].date);
  return note ? `${heading} (${note})` : heading;
}

function toggleDaySelection(day) {
  log(`toggleDaySelection(${day})`);
  if (selectedDays.value.includes(day)) {
    selectedDays.value = selectedDays.value.filter((selected) => selected !== day);
    return;
  }
  selectedDays.value = [...selectedDays.value, day];
}

async function toggleDayView(day) {
  log(`toggleDayView(${day})`);
  if (Array.isArray(day)) return (selectedDays.value = day);
  if (day === "Show All") day = "";
  selectedDays.value = day ? [day] : [];
}

async function updateProgramView() {
  log(`props.session = ${props.session}`);
  program.value = await useProgram({
    timeZoneSelected: timeZoneSelected.value,
    session: props.session,
    dateFrom: props.dateFrom,
    dateTo: props.dateTo,
    year: route.params?.year
  });
}

async function switchTimeZone(newTimeZone) {
  log(`switchTimeZone(${newTimeZone})`);
  // the shared timezone watcher below updates every table on the page
  timeZoneSelected.value = newTimeZone;
}

watch([timeZoneSelected, sheetVersion], async () => await updateProgramView());

const {user} = useUserSafe(); // the account's last zone choice, when signed in
const initProgramView = async () => {
  if (!timeZoneSelected.value) {
    // triggers the watcher above (in every table sharing the ref)
    timeZoneSelected.value = await getDefaultTimeZone(route.params?.year, user.value);
  } else {
    await updateProgramView();
  }
};

log(`session = ${props.session}`);
onMounted(async () => await initProgramView());
</script>

<style scoped>
#programTable {
  clear: both; /* https://stackoverflow.com/q/2580772 */
  margin-top: 3.8em;
  margin-bottom: 2em;
}
/* under the full-width time zone menu of the narrow sidebar's panel (Program/TimeZone.vue): no
   float to clear */
.panel > #timezone + #programTable {
  margin-top: 0.5rem;
}
tr:nth-child(even) {
  /*  https://stackoverflow.com/q/32867605 */
  background-color: var(--background-color-subtle);
}
/* a day's bar, as the accordion's rows were but not a control: green when filtered (Seán,
   2026-10-29) */
.dayBar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0 0.3em;
  margin: 1.5rem 0 0; /* days apart, a section each */
  padding: 0.55em 12px;
  color: var(--text-color);
  background-color: var(--background-color-subtle);
  border-radius: 4px;
  font-size: 1rem;
}
.dayBar.selected {
  color: #1c1c1c;
  background-color: var(--brand-color);
}
.dayBarDate {
  font-weight: 600;
}
.dayBarTitle {
  font-weight: 200;
}
.eventTable {
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 1em;
}
.eventTable td,
th {
  padding: 6px;
  vertical-align: top;
}
/* https://stackoverflow.com/q/27788092 */
.eventTable tr td:first-child {
  width: 19%;
}
.eventTable tr td:nth-child(2) {
  width: 2%;
}
.eventTable tr td:nth-child(3) {
  width: 79%;
}
h4 {
  display: inline;
  font-weight: 600 !important;
}
h5 {
  font-weight: 600 !important;
  /* this don't work - don't know why */
  margin-top: 22px;
  padding-top: 0px;
  margin-bottom: 6px;
  padding-bottom: 0px;
}
.event {
  display: flex; /* outer container for event arrow / title / description */
}
.eventTitle {
  flex-direction: column;
}
span.arrows {
  display: flex;
  flex-direction: column;
  color: #666666;
  padding-left: 0px;
  margin-left: 0px;
  cursor: pointer;
}
/* https://stackoverflow.com/q/27492191 */
.chevronArrow {
  border-right: 0.1em solid #7f7f7f;
  border-top: 0.1em solid #7f7f7f;
  width: 0.4em;
  height: 0.4em;
  margin-top: 0.4em;
  margin-left: 0em;
  margin-right: 0.6em;
  margin-bottom: 0.1em;
}
.hide {
  display: none;
}
.right {
  transform: rotate(45deg);
  -webkit-transform: rotate(45deg);
}
.down {
  transform: rotate(135deg);
  -webkit-transform: rotate(135deg);
}
.down {
  border-color: var(--brand-color); /* open */
}
span.chevronArrow:not(.down):hover {
  border-color: var(--text-color); /* stronger, not the brand colour (DESIGN.md § 15) */
}
/* key adjustments for small screen */
.narrow h4,
.narrow h4 {
  font-size: 1.25rem;
}
.narrow .eventTable col.first,
.medium .eventTable col.first {
  width: 8%;
}
.narrow .eventTable col.second,
.medium .eventTable col.second {
  width: 3%;
}
.narrow .eventTable col.third,
.medium .eventTable col.third {
  width: 89%;
}
.narrow .wideScreenOnly,
.medium .wideScreenOnly {
  /* show only start times on narrow screens */
  display: none;
}
img.streamed {
  width: 14px;
}
.narrowScreenOnly {
  display: none;
}
.narrow .narrowScreenOnly,
.medium .narrowScreenOnly {
  display: inline;
}
/* the play icon: the link colour of the program's video links */
.legendPlay {
  color: color-mix(in srgb, var(--link-color-subtle) 70%, var(--background-color));
  margin-right: 0.15em;
}
/* the legend on the page's own background, an aside, about the program, not of it (DESIGN.md § 4):
   smaller, and halfway from the text's colour to the background's, in either theme (Seán,
   2026-10-29) */
ul.legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  row-gap: 0.25em;
  margin: 12px 0 0;
  padding: 0;
  font-size: 0.9rem;
  color: color-mix(in srgb, var(--text-color) 55%, var(--background-color));
  /* the items' one-line widths don't widen the page: without it, the recorded talk's full label
     made /2026/Program 330px wide on a 320px phone (production, 2026-11-01) */
  contain: inline-size;
}
/* each item on one line, cut short with "…" where it can't fit, so on a phone the legend takes
   two lines at most, the recorded talk's in full (Seán, 2026-10-29) */
ul.legend li {
  min-width: 0;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* a little nearer the text on the dark theme, for contrast (WCAG AA, 4.5:1) */
.darkMode ul.legend {
  color: color-mix(in srgb, var(--text-color) 65%, var(--background-color));
}
/* the first day closer to the legend than the days to each other */
ul.legend + .days .dayList > .dayBlock:first-child .dayBar {
  margin-top: 1rem;
}
/* the days shown slide sideways as the choice changes, the old out as the new comes in, fading
   (Seán, 2026-10-29): 450ms, in step with the pills (DayTags.vue) */
/* back to every day: each day coming grows from nothing, fading in (450ms) */
.dayBlock {
  display: grid;
  grid-template-rows: 1fr;
}
.dayBlockInner {
  min-height: 0;
}
.dayGrow-enter-active .dayBlockInner {
  overflow: hidden;
}
.dayGrow-enter-active {
  transition: grid-template-rows 450ms ease, opacity 450ms ease;
}
.dayGrow-enter-from {
  grid-template-rows: 0fr;
  opacity: 0;
}
.days {
  position: relative;
  overflow-x: clip; /* no sideways scrolling while they slide */
}
.slideLeft-enter-active,
.slideLeft-leave-active,
.slideRight-enter-active,
.slideRight-leave-active {
  transition: transform 450ms ease, opacity 450ms ease;
}
/* the old days faded before the new are in: no text over text */
.slideLeft-leave-active,
.slideRight-leave-active {
  transition-duration: 450ms, 200ms;
}
.slideLeft-enter-active,
.slideRight-enter-active {
  transition-delay: 0ms, 150ms;
}
.slideLeft-leave-active,
.slideRight-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}
.slideLeft-enter-from,
.slideRight-leave-to {
  transform: translateX(40%);
  opacity: 0;
}
.slideLeft-leave-to,
.slideRight-enter-from {
  transform: translateX(-40%);
  opacity: 0;
}
ul.legend li {
  display: inline;
}
img.streamed {
  margin-right: 0.3em;
}
li + li {
  margin-left: 0.6em;
}
li + li::before {
  margin-right: 0.6em;
  opacity: 0.5;
  content: "|"; /* https://stackoverflow.com/q/9171699 */
}
/* on a phone the legend can wrap: a wider gap instead of "|", so no line starts with one */
.narrow ul.legend {
  column-gap: 1.2em; /* a gap, not a margin: a wrapped item starts at the left edge */
}
.narrow li + li {
  margin-left: 0;
}
.narrow li + li::before {
  content: none;
}
</style>
